import React, { useState } from 'react';
import { useAuth } from '../../lib/auth/AuthProvider';
import { useStore } from '../../stores';
import { PanelLeftClose, PanelLeftOpen, Settings, LogOut, User } from 'lucide-react';
import { EnvironmentSelector, EnvironmentManager } from '../../features/environments';
import { CollaborationStatus } from '../../features/presence';
import { WorkspaceSelector, WorkspaceDialog, WorkspaceMembersDialog } from '../../features/workspaces';
import { SettingsDialog } from '../../features/settings';
import { ThemeSelector } from '../../lib/theme';
import { useWorkspaces, useWorkspaceMembers } from '../../hooks';
import { isElectron } from '../../lib/http/httpClient';

export function Header() {
  const { userInfo, logout } = useAuth();
  const { sidebarCollapsed, toggleSidebar } = useStore();
  const [envManagerOpen, setEnvManagerOpen] = useState(false);
  const [editingEnvId, setEditingEnvId] = useState<string | null>(null);
  const [settingsOpen, setSettingsOpen] = useState(false);

  // Workspace state
  const {
    workspaces,
    activeWorkspace,
    isLoading: workspacesLoading,
    createWorkspace,
    updateWorkspace,
    deleteWorkspace,
  } = useWorkspaces();
  const [workspaceDialogOpen, setWorkspaceDialogOpen] = useState(false);
  const [editingWorkspace, setEditingWorkspace] = useState<typeof activeWorkspace>(null);
  const [membersDialogOpen, setMembersDialogOpen] = useState(false);
  const [membersWorkspaceId, setMembersWorkspaceId] = useState<string | null>(null);
  const { members, isLoading: membersLoading, addMember } = useWorkspaceMembers(membersDialogOpen ? membersWorkspaceId : null);

  const handleEditEnvironment = (envId: string) => {
    setEditingEnvId(envId);
    setEnvManagerOpen(true);
  };

  const handleCreateEnvironment = () => {
    setEditingEnvId(null);
    setEnvManagerOpen(true);
  };

  const handleEnvManagerClose = (open: boolean) => {
    setEnvManagerOpen(open);
    if (!open) {
      setEditingEnvId(null);
    }
  };

  // Workspace handlers
  const handleCreateWorkspace = () => {
    setEditingWorkspace(null);
    setWorkspaceDialogOpen(true);
  };

  const handleManageWorkspace = (workspaceId: string) => {
    const workspace = Array.isArray(workspaces) ? workspaces.find((w) => w.id === workspaceId) : undefined;
    setEditingWorkspace(workspace || null);
    setWorkspaceDialogOpen(true);
  };

  const handleManageMembers = (workspaceId: string) => {
    setMembersWorkspaceId(workspaceId);
    setMembersDialogOpen(true);
  };

  const handleWorkspaceSubmit = async (data: { name: string; description?: string }) => {
    if (editingWorkspace) {
      await updateWorkspace(editingWorkspace.id, data);
    } else {
      await createWorkspace(data.name, data.description);
    }
    setWorkspaceDialogOpen(false);
  };

  const handleWorkspaceDelete = async () => {
    if (editingWorkspace) {
      await deleteWorkspace(editingWorkspace.id);
      setWorkspaceDialogOpen(false);
    }
  };

  const handleAddMember = async (email: string, role: 'admin' | 'member') => {
    await addMember(email, role);
  };

  return (
    <header
      className={`h-9 border-b border-border/50 flex items-center justify-between pr-3 bg-card ${
        isElectron() ? 'pl-20' : 'pl-3'
      }`}
      style={isElectron() ? { WebkitAppRegion: 'drag' } as React.CSSProperties : undefined}
    >
      {/* Left */}
      <div className="flex items-center gap-3" style={isElectron() ? { WebkitAppRegion: 'no-drag' } as React.CSSProperties : undefined}>
        <button
          onClick={toggleSidebar}
          className="p-1 rounded-md hover:bg-accent transition-colors"
          title={sidebarCollapsed ? 'Show Sidebar' : 'Hide Sidebar'}
        >
          {sidebarCollapsed ? (
            <PanelLeftOpen className="w-4 h-4 text-muted-foreground" />
          ) : (
            <PanelLeftClose className="w-4 h-4 text-muted-foreground" />
          )}
        </button>

        <div className="flex items-center gap-2">
          <span className="text-sm font-semibold">🐌 Snailman</span>
        </div>

        {/* Workspace Selector */}
        <WorkspaceSelector
          workspaces={workspaces}
          onCreateWorkspace={handleCreateWorkspace}
          onManageWorkspace={handleManageWorkspace}
          onManageMembers={handleManageMembers}
        />
      </div>

      {/* Center - Environment Selector */}
      <div className="flex items-center" style={isElectron() ? { WebkitAppRegion: 'no-drag' } as React.CSSProperties : undefined}>
        <EnvironmentSelector
          onEditEnvironment={handleEditEnvironment}
          onCreateEnvironment={handleCreateEnvironment}
        />
      </div>

      {/* Right - Collaboration Status and User Menu */}
      <div className="flex items-center gap-2" style={isElectron() ? { WebkitAppRegion: 'no-drag' } as React.CSSProperties : undefined}>
        {/* Collaboration Status */}
        <CollaborationStatus />

        {/* Theme Selector */}
        <ThemeSelector />

        <button
          onClick={() => setSettingsOpen(true)}
          className="p-1 rounded-md hover:bg-accent transition-colors"
          title="Settings"
        >
          <Settings className="w-4 h-4 text-muted-foreground" />
        </button>

        <div className="relative group">
          <button className="flex items-center gap-2 p-1 rounded-md hover:bg-accent transition-colors">
            <User className="w-4 h-4 text-muted-foreground" />
            <span className="text-sm text-foreground max-w-[150px] truncate">
              {userInfo?.displayName || 'User'}
            </span>
          </button>

          <div className="absolute right-0 top-full mt-1 w-48 bg-card border border-border/50 rounded-md shadow-lg opacity-0 invisible group-hover:opacity-100 group-hover:visible transition-all z-50">
            <div className="p-2 border-b border-border/50">
              <p className="text-sm font-medium text-foreground truncate">
                {userInfo?.displayName || 'User'}
              </p>
              {userInfo?.email && (
                <p className="text-xs text-muted-foreground truncate">
                  {userInfo.email}
                </p>
              )}
            </div>
            <div className="p-1">
              <button
                onClick={logout}
                className="w-full flex items-center gap-2 px-3 py-2 text-sm text-destructive hover:bg-destructive/10 rounded-md transition-colors"
              >
                <LogOut className="w-4 h-4" />
                Sign out
              </button>
            </div>
          </div>
        </div>
      </div>

      {/* Environment Manager Modal */}
      <EnvironmentManager
        open={envManagerOpen}
        onOpenChange={handleEnvManagerClose}
        initialEnvironmentId={editingEnvId}
      />

      {/* Workspace Dialog */}
      <WorkspaceDialog
        open={workspaceDialogOpen}
        onOpenChange={setWorkspaceDialogOpen}
        workspace={editingWorkspace}
        onSubmit={handleWorkspaceSubmit}
        onDelete={editingWorkspace?.role === 'owner' ? handleWorkspaceDelete : undefined}
        isLoading={workspacesLoading}
      />

      {/* Workspace Members Dialog */}
      <WorkspaceMembersDialog
        open={membersDialogOpen}
        onOpenChange={setMembersDialogOpen}
        workspaceName={(Array.isArray(workspaces) ? workspaces.find((w) => w.id === membersWorkspaceId) : undefined)?.name || ''}
        members={members}
        currentUserRole={(Array.isArray(workspaces) ? workspaces.find((w) => w.id === membersWorkspaceId) : undefined)?.role || 'member'}
        onAddMember={handleAddMember}
        isLoading={membersLoading}
      />

      {/* Settings Dialog */}
      <SettingsDialog
        open={settingsOpen}
        onOpenChange={setSettingsOpen}
      />
    </header>
  );
}
